import { getPagePermissionByPath } from './utils'
import { joinRoutePrefix, ROUTE_PREFIX } from '../config/routePrefixes'

export const ADMIN_PERMISSION_SECTION = 'admin'
export const OPERATION_PERMISSION_SECTION = 'stock-management-operation'

/**
 * Where a login lands before the shell picks the user's first page. No `/operations` path belongs
 * here: entering that shell re-runs getRest, so its dashboard would bounce to the admin's.
 */
const POST_LOGIN_PATHS = new Set([
  '/',
  '/login',
  ROUTE_PREFIX.ADMIN,
  joinRoutePrefix(ROUTE_PREFIX.ADMIN, '/dashboard'),
])

/** Sort by the `order` field like the sidebar does; entries without one go last. */
const sortByOrder = (pages) =>
  [...pages].sort((a, b) => {
    const ao = a?.order ?? null
    const bo = b?.order ?? null
    if (ao === null && bo === null) return 0
    if (ao === null) return 1
    if (bo === null) return -1
    return ao - bo
  })

/** First navigable page route in a permission tree (depth-first by `order`, unprefixed paths). */
export function getFirstAccessibleRoute(pages) {
  if (!Array.isArray(pages) || pages.length === 0) return null
  for (const page of sortByOrder(pages)) {
    if (page?.isActive === false) continue
    if (page.pageType === 'page' && page.route) return page.route
    if (Array.isArray(page.subPage) && page.subPage.length > 0) {
      const nested = getFirstAccessibleRoute(page.subPage)
      if (nested) return nested
    }
    if (page.route && page.pageType === 'layout') return page.route
  }
  return null
}

/** First route from DefaultLayout's flattened `access` list (already shell-prefixed). */
export function getFirstAccessibleRouteFromFlatAccess(accessList) {
  if (!Array.isArray(accessList) || accessList.length === 0) return null
  const page = accessList.find(
    (p) => p?.isActive !== false && p?.route && p?.pageType === 'page'
  )
  if (page?.route) return page.route
  const withRoute = accessList.find((p) => p?.isActive !== false && p?.route)
  return withRoute?.route || null
}

export function resolvePostLoginPath(adminPerms, operationPerms) {
  const adminRoute = getFirstAccessibleRoute(adminPerms)
  if (adminRoute) return joinRoutePrefix(ROUTE_PREFIX.ADMIN, adminRoute)
  const opRoute = getFirstAccessibleRoute(operationPerms)
  if (opRoute) return joinRoutePrefix(ROUTE_PREFIX.OPERATIONS, opRoute)
  if (operationPerms?.length > 0) {
    return joinRoutePrefix(ROUTE_PREFIX.OPERATIONS, '/dashboard')
  }
  return null
}

export function shouldRedirectPostLogin(
  pathname,
  adminPerms,
  operationPerms,
  permissionTrees,
  restaurantAccess,
  restaurantUuid
) {
  if (POST_LOGIN_PATHS.has(pathname)) return true
  const isAdminShell =
    pathname === ROUTE_PREFIX.ADMIN || pathname.startsWith(`${ROUTE_PREFIX.ADMIN}/`)
  if (isAdminShell && !adminPerms?.length && operationPerms?.length > 0) return true
  // Checked against the admin tree, so only for admin paths.
  if (isAdminShell && pathname.endsWith('/dashboard')) {
    const hasAdminDashboard = restaurantHasPageByPath(
      permissionTrees,
      restaurantAccess,
      restaurantUuid,
      '/dashboard',
      ADMIN_PERMISSION_SECTION
    )
    if (!hasAdminDashboard && adminPerms?.length > 0) return true
    if (!hasAdminDashboard && !adminPerms?.length && operationPerms?.length > 0) return true
  }
  return false
}

export function filterPagesByModules(pages, moduleKeys) {
  if (!Array.isArray(pages) || !Array.isArray(moduleKeys) || moduleKeys.length === 0) return []
  return pages
    .map((page) => {
      const modules = Array.isArray(page?.modules) ? page.modules : []
      if (!moduleKeys.some((value) => modules.includes(value))) return null
      const next = { ...page }
      if (Array.isArray(page.subPage)) {
        next.subPage = filterPagesByModules(page.subPage, moduleKeys)
        if (next.pageType !== 'page' && next.subPage.length === 0) return null
      }
      return next
    })
    .filter(Boolean)
}

export function parseRestaurantPermissionPayload(data) {
  if (!data) return { permissionTrees: {}, restaurantAccess: [] }

  const permissionTrees = { ...(data.permissionsBySection || {}) }
  if (Array.isArray(data.availableSections)) {
    data.availableSections.forEach((sec) => {
      if (permissionTrees[sec]) return
      const key = `permissions${String(sec).charAt(0).toUpperCase()}${String(sec).slice(1)}`
      if (data[key]) permissionTrees[sec] = data[key]
    })
  }
  if (!permissionTrees[ADMIN_PERMISSION_SECTION] && data.permissionsAdmin) {
    permissionTrees[ADMIN_PERMISSION_SECTION] = data.permissionsAdmin
  }
  if (!permissionTrees[OPERATION_PERMISSION_SECTION] && data['permissionsStock-management-operation']) {
    permissionTrees[OPERATION_PERMISSION_SECTION] = data['permissionsStock-management-operation']
  }

  if (Array.isArray(data.restaurantAccess)) {
    return { permissionTrees, restaurantAccess: data.restaurantAccess }
  }

  return { permissionTrees, restaurantAccess: [] }
}

export function getRestaurantPermissions(permissionTrees, restaurantAccess, restaurantUuid, section) {
  const access = (restaurantAccess || []).find((row) => row.restaurantUuid === restaurantUuid)
  const tree = permissionTrees?.[section] || []
  return filterPagesByModules(tree, access?.moduleKeys || [])
}

export function getRestaurantPermissionsAllSections(permissionTrees, restaurantAccess, restaurantUuid) {
  const pages = []
  Object.keys(permissionTrees || {}).forEach((section) => {
    pages.push(...getRestaurantPermissions(permissionTrees, restaurantAccess, restaurantUuid, section))
  })
  return pages
}

export function restaurantHasPageByPath(permissionTrees, restaurantAccess, restaurantUuid, pathname, section = ADMIN_PERMISSION_SECTION) {
  const perms = getRestaurantPermissions(permissionTrees, restaurantAccess, restaurantUuid, section)
  return Boolean(getPagePermissionByPath(perms, pathname))
}

export function restaurantsWithPageByPath(restaurants, permissionTrees, restaurantAccess, pathname, section = ADMIN_PERMISSION_SECTION) {
  return (restaurants || []).filter((rest) =>
    restaurantHasPageByPath(permissionTrees, restaurantAccess, rest.uuid, pathname, section)
  )
}

export function flagRestaurantsWithoutPage(restaurants, permissionTrees, restaurantAccess, pathname, section = ADMIN_PERMISSION_SECTION) {
  return (restaurants || []).map((rest) => ({
    ...rest,
    isDisabled: rest.uuid === 'all-restaurants'
      ? false
      : !restaurantHasPageByPath(permissionTrees, restaurantAccess, rest.uuid, pathname, section),
  }))
}
